let stream = null;
let streamReady = false;
let screenStream = null;
let initStarted = false;
let handedOff = false;
let hiddenVideo = null;
let claimedResources = null;

export function isStreamLive(mediaStream) {
  return !!(
    mediaStream?.active &&
    mediaStream.getTracks().some((track) => track.readyState === 'live')
  );
}

function stopHiddenVideo() {
  if (hiddenVideo) {
    hiddenVideo.srcObject = null;
    hiddenVideo = null;
  }
}

async function warmCamera() {
  try {
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    if (handedOff) {
      mediaStream.getTracks().forEach((t) => t.stop());
      return;
    }
    stream = mediaStream;
    streamReady = isStreamLive(mediaStream);

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.srcObject = mediaStream;
    hiddenVideo = video;

    video.onloadeddata = () => {
      streamReady = isStreamLive(stream);
    };

    await video.play().catch(() => {});
    streamReady = isStreamLive(stream);
  } catch (err) {
    console.warn('Interview prep: camera warm failed (non-blocking)', err);
  }
}

/** Start background camera pre-warm during consent (no MediaPipe — fresh FD in interview room). */
const yieldToBrowser = (ms = 60) => new Promise((resolve) => setTimeout(resolve, ms));

let prewarmedObjectDetectionModel = null;
let objectDetectionPrewarmPromise = null;

export async function prewarmObjectDetection() {
  if (prewarmedObjectDetectionModel) return prewarmedObjectDetectionModel;
  if (objectDetectionPrewarmPromise) return objectDetectionPrewarmPromise;

  objectDetectionPrewarmPromise = (async () => {
    try {
      console.log('🔄 Sliced loading of COCO-SSD object detection (watchdog-safe)...');

      // Step 1: Import TensorFlow.js & yield to reset Chrome 5s watchdog
      const tf = await import('@tensorflow/tfjs');
      await yieldToBrowser(60);

      // Step 2: Import COCO-SSD & yield to reset Chrome 5s watchdog
      const cocoSsd = await import('@tensorflow-models/coco-ssd');
      await yieldToBrowser(60);

      // Step 3: Initialize WebGL backend & yield to reset Chrome 5s watchdog
      await tf.ready();
      await yieldToBrowser(60);

      // Step 4: Load model architecture & weights & yield to reset Chrome 5s watchdog
      const model = await cocoSsd.load({ base: 'lite_mobilenet_v2' });
      await yieldToBrowser(60);

      // Step 5: Warm up with 1 dummy frame to compile GPU shaders safely
      const dummyCanvas = document.createElement('canvas');
      dummyCanvas.width = 64;
      dummyCanvas.height = 64;
      const ctx = dummyCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, 64, 64);
        await model.detect(dummyCanvas);
      }
      await yieldToBrowser(60);

      prewarmedObjectDetectionModel = model;
      console.log('✅ COCO-SSD object detection loaded cleanly with event-loop slicing');
      return model;
    } catch (e) {
      console.warn('⚠️ Object detection loading failed (non-blocking):', e);
      return null;
    } finally {
      objectDetectionPrewarmPromise = null;
    }
  })();

  return objectDetectionPrewarmPromise;
}

export function getPrewarmedObjectDetection() {
  return prewarmedObjectDetectionModel;
}

export function isObjectDetectionReady() {
  return !!prewarmedObjectDetectionModel;
}

export async function waitForObjectDetectionReady(timeoutMs = 4000) {
  if (prewarmedObjectDetectionModel) return true;
  if (objectDetectionPrewarmPromise) {
    const timeout = new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs));
    const warmed = await Promise.race([objectDetectionPrewarmPromise, timeout]);
    return !!warmed || !!prewarmedObjectDetectionModel;
  }
  return true;
}

export function startInterviewPrep() {
  if (initStarted) return;
  initStarted = true;
  void warmCamera();
}

export function getInterviewPrep() {
  return {
    stream,
    screenStream,
    faceDetection: null,
    ready: streamReady && isStreamLive(stream),
    initStarted,
  };
}

export function isInterviewPrepHandedOff() {
  return handedOff;
}

/** Register the live stream from system check so handoff can claim it. */
export function registerInterviewPrepInstances({
  stream: nextStream,
  screenStream: nextScreenStream,
} = {}) {
  if (nextStream && isStreamLive(nextStream)) {
    stream = nextStream;
    streamReady = true;
  }
  if (nextScreenStream && isStreamLive(nextScreenStream)) {
    screenStream = nextScreenStream;
  }
}

/**
 * Claim the live camera (+ optional screen) stream for the interview/coding room.
 * FaceDetection is always created fresh in ProctoringMonitor (WebGL tied to video element).
 */
export function claimInterviewResources({
  stream: explicitStream,
  screenStream: explicitScreenStream,
} = {}) {
  if (claimedResources) {
    return claimedResources;
  }

  const resolvedStream = isStreamLive(explicitStream)
    ? explicitStream
    : isStreamLive(stream)
      ? stream
      : explicitStream || stream;

  const resolvedScreen = isStreamLive(explicitScreenStream)
    ? explicitScreenStream
    : isStreamLive(screenStream)
      ? screenStream
      : explicitScreenStream || screenStream;

  claimedResources = {
    stream: resolvedStream,
    screenStream: resolvedScreen,
    faceDetection: null,
    ready: isStreamLive(resolvedStream),
  };

  handedOff = true;
  stopHiddenVideo();
  stream = null;
  screenStream = null;
  streamReady = false;
  initStarted = false;

  return claimedResources;
}

export function getClaimedInterviewResources() {
  return claimedResources;
}

/** @deprecated Use claimInterviewResources from parent handoff. */
export function takeInterviewPrepForInterview() {
  return claimInterviewResources();
}

/** Call right before navigating to the interview room. */
export function markInterviewPrepHandedOff() {
  handedOff = true;
  stopHiddenVideo();
}

/** Release claimed resources when the interview session ends. */
export function releaseClaimedInterviewResources() {
  if (!claimedResources) return;
  const rec = claimedResources.videoRecorder;
  if (rec) {
    try {
      if (typeof rec.stopAsync === 'function') void rec.stopAsync().catch(() => {});
      else if (typeof rec.stop === 'function') rec.stop();
    } catch (_) {}
  }
  if (claimedResources.stream) {
    claimedResources.stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (_) {}
    });
  }
  if (claimedResources.screenStream) {
    claimedResources.screenStream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (_) {}
    });
  }
  claimedResources = null;
}

/**
 * Stash the live MediaRecorder for AI → coding so one continuous SessionVideo
 * is uploaded at the end of coding (interview_coding only).
 */
export function stashContinuousSessionHandoff({
  videoRecorder = null,
  recordingStartTime = null,
  stream: nextStream = null,
} = {}) {
  if (!claimedResources) {
    claimedResources = {
      stream: null,
      screenStream: null,
      faceDetection: null,
      ready: false,
    };
  }
  if (videoRecorder) {
    claimedResources.videoRecorder = videoRecorder;
  }
  if (recordingStartTime != null) {
    claimedResources.recordingStartTime = recordingStartTime;
  }
  if (nextStream && isStreamLive(nextStream)) {
    claimedResources.stream = nextStream;
    claimedResources.ready = true;
  }
  return claimedResources;
}

/** Peek the continuous recorder without clearing (coding room adopts it). */
export function peekContinuousVideoRecorder() {
  return claimedResources?.videoRecorder || null;
}

/** Clear recorder pointer after coding room has taken ownership (tracks stay live). */
export function clearStashedVideoRecorder() {
  if (claimedResources) {
    claimedResources.videoRecorder = null;
    claimedResources.recordingStartTime = null;
  }
}

/** Release prep resources when candidate leaves before the interview. */
export function releaseInterviewPrep() {
  if (handedOff || claimedResources) return;
  stopHiddenVideo();
  if (stream) {
    stream.getTracks().forEach((t) => t.stop());
    stream = null;
  }
  if (screenStream) {
    screenStream.getTracks().forEach((t) => t.stop());
    screenStream = null;
  }
  streamReady = false;
  initStarted = false;
}
